import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import { NotAuthenticated } from '../core/guards/not-authenticated.guard'
import { IsAuthenticated } from '../core/guards/is-authenticated.guard'
import { paFlagGuard } from './personal-assistant/access/pa-flag.guard'
import { ROUTE_DATA } from '../core/helpers/variables'
import { isAutomaticGuard } from '../core/guards/is-automatic.guard'
import { nativeDeviceGuard } from '../core/guards/native-device.guard'
import { Permission } from '../core/enums/permission.enum'
import { userGuard } from '../core/guards/user.guard'
import {
  isAuthenticatedCanMatch,
  rpmUserCanMatch,
  rpmUserGuard
} from './personal-assistant/access/rpm-user.guard'
import { PA_DEV_ROUTES } from './_qa-devtools/pa-dev-routes'
import { RouteName } from '../core/enums/route-name.enum'
import { HoverPreloadStrategy } from 'ngx-hover-preload'
import { RouteParamName } from '../core/enums/route-param-name.enum'
import { rpmTabletGuard } from '../core/guards/rpm-tablet.guard'
import { residentDeactivateGuard } from '../core/guards/resident-deactivate.guard'

const routes: Routes = [
  {
    path: RouteName.Login,
    canActivate: [NotAuthenticated],
    canLoad: [NotAuthenticated],
    loadComponent: () =>
      import('./pages/auth/components/login/login.component').then(
        c => c.LoginComponent
      )
  },
  {
    path: RouteName.LoginRpm,
    canActivate: [NotAuthenticated],
    canLoad: [NotAuthenticated],
    loadComponent: () => import('./pages/login-rpm/login-rpm.component')
  },
  {
    path: RouteName.Reset,
    loadComponent: () =>
      import(
        './pages/reset-password/components/reset-password/reset-password.component'
      ).then(c => c.ResetPasswordComponent)
  },
  {
    path: RouteName.Devices,
    canLoad: [IsAuthenticated, isAutomaticGuard],
    canActivate: [IsAuthenticated, isAutomaticGuard],
    data: {
      ...ROUTE_DATA.devices
    },
    loadComponent: () =>
      import('./pages/devices/components/devices/devices.component').then(
        c => c.DevicesComponent
      )
  },
  {
    path: RouteName.Insights,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated],
    data: {
      ...ROUTE_DATA.patient
    },
    loadComponent: () =>
      import('./pages/insights/components/insights/insights.component').then(
        c => c.InsightsComponent
      )
  },
  {
    path: RouteName.MyPlan,
    // Gate: the PA flag and allowlist (paFlagGuard) AND an RPM user (rpmUserCanMatch, rpmUserGuard,
    // which send other users to /patients). No `userGuard`: setPermissionsBasedUserType gives RPM
    // users (`_userType: 'organization-user'`) Permission.Admin, and userGuard requires
    // Permission.User, so it would redirect the very users this page is for. `canMatch`, not
    // `canLoad`: canLoad never runs for loadComponent. canMatch blocks a deep link before the chunk
    // loads, and canActivate re-checks on activation. The ?ff_pa=1 switch (non-production builds
    // only) does not bypass the role check.
    // isAuthenticatedCanMatch runs first so an unauthenticated deep link goes to login instead of
    // waiting for a profile that never arrives.
    canMatch: [isAuthenticatedCanMatch, rpmUserCanMatch],
    canActivate: [IsAuthenticated, paFlagGuard, rpmUserGuard],
    loadComponent: () =>
      import('./pages/my-plan/my-plan.component').then(c => c.MyPlanComponent)
  },
  // Dev-only PA routes: empty unless angular.json swaps in pa-dev-routes.enabled.ts (dev, qa and
  // quickfix configurations only).
  ...PA_DEV_ROUTES,
  {
    path: `${RouteName.Patient}/:patientId`,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated],
    data: {
      ...ROUTE_DATA.patient
    },
    loadComponent: () =>
      import('./pages/patient/component/patient/patient.component').then(
        c => c.PatientComponent
      )
  },
  {
    path: `${RouteName.Insight}/:insightId`,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated],
    data: {
      ...ROUTE_DATA.patient
    },
    loadComponent: () =>
      import('./pages/insight/components/insight/insight.component').then(
        c => c.InsightComponent
      )
  },
  {
    path: RouteName.Patients,
    canLoad: [IsAuthenticated, nativeDeviceGuard],
    canActivate: [IsAuthenticated, nativeDeviceGuard],
    data: {
      ...ROUTE_DATA.all_patients
    },
    loadComponent: () =>
      import(
        './pages/all-patients/components/all-patients/all-patients.component'
      ).then(c => c.AllPatientsComponent)
  },
  {
    path: RouteName.Reports,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated],
    data: {
      ...ROUTE_DATA.patient,
      permissions: {
        only: Permission.User
      }
    },
    loadComponent: () =>
      import('./pages/reports/components/reports/reports.component').then(
        c => c.ReportsComponent
      )
  },
  {
    path: RouteName.RpmTabletDashboard,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated, rpmTabletGuard],
    data: {
      ...ROUTE_DATA.patient
    },
    loadComponent: () =>
      import('./pages/rpm-tablet-dashboard/rpm-tablet-dashboard.component')
  },
  {
    path: RouteName.Alerts,
    canLoad: [IsAuthenticated, isAutomaticGuard],
    canActivate: [IsAuthenticated, isAutomaticGuard],
    data: {
      ...ROUTE_DATA.alerts
    },
    loadComponent: () =>
      import('./pages/alerts/components/alerts/alerts.component').then(
        c => c.AlertsComponent
      )
  },
  {
    path: `${RouteName.Report}/:id`,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated],
    loadComponent: () =>
      import('./pages/report/report.component').then(c => c.ReportComponent)
  },
  {
    path: RouteName.Notifications,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated],
    loadComponent: () =>
      import(
        './pages/notifications/components/notifications/notifications.component'
      ).then(c => c.NotificationsComponent)
  },
  {
    path: `${RouteName.ZoomCall}/:id`,
    loadComponent: () => import('./pages/zoom-call/zoom-call.component')
  },
  {
    path: `${RouteName.Resident}/:${RouteParamName.ResidentId}`,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated],
    canDeactivate: [residentDeactivateGuard],
    data: {
      ...ROUTE_DATA.patient
    },
    loadComponent: () => import('./pages/resident/resident.component'),
    loadChildren: () => import('./pages/resident/routes/routes')
  },
  {
    path: `${RouteName.MonthlySummary}`,
    canLoad: [IsAuthenticated],
    canActivate: [IsAuthenticated],
    loadComponent: () =>
      import('./pages/monthly-summary/monthly-summary.component')
  },
  {
    path: RouteName.Home,
    canLoad: [IsAuthenticated, nativeDeviceGuard],
    canActivate: [IsAuthenticated, nativeDeviceGuard, userGuard],
    data: {
      ...ROUTE_DATA.patients,
      permissions: {
        only: Permission.User
      }
    },
    loadComponent: () =>
      import('./pages/patients/components/patients/patients.component').then(
        c => c.PatientsComponent
      )
  }
]

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: false,
      preloadingStrategy: HoverPreloadStrategy,
      paramsInheritanceStrategy: 'always',
      errorHandler: error => {
        console.warn('Navigation error occurred. Reloading...', error)
        window.location.reload()
      }
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
